import { useEffect, useRef } from 'react';

export type SoundParams = {
	name: string;
	looped?: boolean;
	autoplay?: boolean;
	volume?: number; // 0...1, default: 1
};

export type ClipApi = {
	play: () => void;
	pause: () => void;
	start: () => void;
	stop: () => void;
	setVolume: (volume: number) => void;
};

const SOUND_PATH = '/sfx/';
const SOUND_EXTENSION = '.mp3';

function createClipApiFromAudio(params: SoundParams): ClipApi {
	const src = new URL(`${SOUND_PATH}${params.name}${SOUND_EXTENSION}`, import.meta.url).href;
	const audio = new Audio(src);

	audio.loop = params.looped ?? false;
	audio.volume = params.volume ?? 1;

	const safePlay = () => {
		const playPromise = audio.play();
		playPromise.catch((e) => {
			if (e.name === 'AbortError') return; // ignore pauses and stops
			console.warn('Audio playback failed:', e);
		});
	};

	return {
		play() {
			safePlay();
		},
		pause() {
			audio.pause();
		},
		start() {
			audio.currentTime = 0;
			safePlay();
		},
		stop() {
			audio.pause();
			audio.currentTime = 0;
		},
		setVolume(volume: number) {
			audio.volume = volume;
		},
	};
}

export function playSound(params: SoundParams): ClipApi {
	const clip = createClipApiFromAudio(params);

	const shouldAutoplay = params.autoplay ?? true;
	if (shouldAutoplay) {
		clip.play();
	}

	return clip;
}

export function useSound(params: SoundParams): ClipApi {
	const clipRef = useRef<ClipApi | null>(null);

	useEffect(() => {
		const clip = playSound({ ...params });
		clipRef.current = clip;

		return () => {
			clip.stop();
			clipRef.current = null;
		};
	}, [params.name]);

	useEffect(() => {
		if (params.volume && clipRef.current) {
			clipRef.current.setVolume(params.volume);
		}
	}, [params.volume]);

	const api: ClipApi = {
		play: () => clipRef.current?.play(),
		pause: () => clipRef.current?.pause(),
		start: () => clipRef.current?.start(),
		stop: () => clipRef.current?.stop(),
		setVolume: (v: number) => clipRef.current?.setVolume(v),
	};

	return api;
}
